import { Table, TableBody, TableCell, TableContainer } from "@mui/material";
import { useCallback, useMemo } from "react";
import {
  EMPTY_STRING,
  localeStringOptions,
} from "../../../common/constants/common.constants";
import ClubTableCell from "../ClubTableCell/ClubTableCell";
import DashboardTableCell from "../DashboardTable/DashboardTableCell/DashboardTableCell";
import TableHeading from "../DashboardTable/TableHeading/TableHeading";
import {
  rowCellStyle,
  tableWrapper,
} from "../DashboardTable/TableHeading/TableHeading.styles";
import TableInfo from "../TableInfo/TableInfo";
import TableTitleAndFilters from "../TableTitleAndFilters/TableTitleAndFilters";
import treasuryIcon from "../../../assets/treasuries.svg";
import { IMembersClubData } from "../../../common/interfaces/user-dashboard.interface";
import DashboardClaimButton from "../DashboardClaimButton/DashboardClaimButton";
import { getTreasurySymbol, round } from "../../../utilities/helpers";
import RoleTableCell from "../RoleTableCell/RoleTableCell";
import useModifier from "../../../hooks/useModifier";
import "./TreasuryTable.scss";
import DashboardTableRow from "../DashboardTable/DashboardTableRow/DashboardTableRow";
import { CLUB_DETAILS } from "../../../common/constants/routes.constants";
import { TREASURY_TABLE_COLUMNS } from "../../../common/constants/dashboard.constants";
import { DashboardFilter } from "../../../common/enums/dashboard.enums";
import { dashboardStore } from "../../../contexts/dashboardStore";
import { solanaApiLogo } from "../../../solana/common/utilities";

const TreasuryTable = () => {
  const { treasuryData, clubs, withdrawals } = dashboardStore();

  const { filteredItems, addFilterByName, addFilterItem } = useModifier(
    clubs ?? [],
    DashboardFilter.FundingStatus,
    treasuryData
  );

  const calculateAvailableClaimPerTreasury = useCallback(
    (clubAddress: string) => {
      const withdrawalsPerClub = withdrawals!.filter(
        (withdrawal) => withdrawal.club.clubAddress === clubAddress
      );
      let amount = 0;
      const nonClaimdWithdrawals = withdrawalsPerClub.filter(
        (w) => !w.hasWithdrawn
      );
      if (nonClaimdWithdrawals.length > 0) {
        amount = nonClaimdWithdrawals.reduce(
          (prev, next) => prev + next.withdrawalRecordAmount,
          0
        );
      } else {
        amount = withdrawalsPerClub?.reduce(
          (prev, next) => prev + next.withdrawalRecordAmount,
          0
        );
      }
      return round(amount);
    },
    [withdrawals]
  );

  const getRelatedTreasury = useCallback(
    (clubData: IMembersClubData) => {
      return treasuryData?.find(
        (treasury) => treasury?.club.clubAddress === clubData.club.clubAddress
      );
    },
    [treasuryData]
  );

  const renderTreasuryData = useMemo(() => {
    return filteredItems!.map((clubData) => {
      const relatedTreasury = getRelatedTreasury(clubData);
      const relatedWithdrawal = calculateAvailableClaimPerTreasury(
        clubData.club.clubAddress
      );
      return (
        <DashboardTableRow
          key={clubData.club.clubAddress}
          redirectUrl={`${CLUB_DETAILS}/${clubData.club.clubAddress}`}
        >
          <ClubTableCell
            clubImageUrl={clubData.club.clubImageUrl}
            clubName={clubData.club.clubName}
          />
          <RoleTableCell role={clubData.role} />
          <DashboardTableCell
            value={
              relatedTreasury !== null
                ? getTreasurySymbol(relatedTreasury?.denominatedTokenInfo)
                : null
            }
            avatar={
              relatedTreasury !== null
                ? relatedTreasury?.denominatedTokenInfo?.image ?? solanaApiLogo
                : undefined
            }
          />
          <DashboardTableCell
            value={
              relatedTreasury !== null
                ? relatedTreasury?.balance.toLocaleString(
                    undefined,
                    localeStringOptions
                  )
                : null
            }
          />
          <DashboardTableCell
            value={
              relatedTreasury !== null ? `${relatedTreasury?.ownership}%` : null
            }
          />
          <TableCell style={rowCellStyle}>
            <div
              className={`${
                relatedTreasury?.isFundraisingInProgress
                  ? "treasury-table__wrapper treasury-table__wrapper--active"
                  : "treasury-table__wrapper"
              }`}
            >
              <p
                className={`${
                  relatedTreasury?.isFundraisingInProgress
                    ? "treasury-table__text treasury-table__text--active"
                    : "treasury-table__text"
                }`}
              >
                {relatedTreasury?.isFundraisingInProgress ? "Yes" : "No"}
              </p>
            </div>
          </TableCell>
          <DashboardTableCell
            value={
              relatedTreasury !== null
                ? relatedTreasury?.deposits.toLocaleString(
                    undefined,
                    localeStringOptions
                  )
                : null
            }
          />
          {relatedWithdrawal && relatedWithdrawal > 0 ? (
            <DashboardClaimButton
              clubAddress={clubData.club.clubAddress}
              isTreasuryWithdrawal
            />
          ) : (
            <DashboardTableCell value={EMPTY_STRING} />
          )}
        </DashboardTableRow>
      );
    });
  }, [filteredItems, treasuryData, withdrawals]);

  return (
    <div className="treasury-table">
      <TableInfo
        icon={treasuryIcon}
        title="Total number of treasuries"
        description={`${
          treasuryData?.filter((item) => item !== null).length
        } Treasuries`}
      />
      <TableTitleAndFilters
        tableTitle="List of all your clubs' Treasuries"
        filtersData={[
          {
            onFilterSelect: () => {
              addFilterItem(true);
            },
            filterTitle: "Fundraising active",
          },
          //TODO@goran:Implement this filtering later!
          // {
          //   filterTitle: "Claim available",
          //   onFilterSelect: () => {
          //     addFilterItem(false);
          //   },
          // },
        ]}
      />
      <TableContainer style={tableWrapper}>
        <Table>
          <TableHeading
            showInputField
            columns={TREASURY_TABLE_COLUMNS}
            onInput={(e) => addFilterByName(e.target.value)}
          />
          <TableBody>{renderTreasuryData}</TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default TreasuryTable;
